/*
  mapper.js — Column name normalizer
  
  Different AIS data providers use different column names for the same data.
  This module translates any provider's column names into our standard internal format.
  
  To add a new provider: add a new entry to the PROVIDERS object below.
  Nothing else in the app needs to change.

  Standard internal field names we use everywhere:
  - mmsi        : vessel unique ID number
  - vesselName  : vessel name
  - timestamp   : date and time of the position report
  - lat         : latitude
  - lng         : longitude
  - speed       : speed over ground
  - heading     : compass heading
  - course      : course over ground
  - vesselType  : type of vessel
  - imo         : IMO number
  - callSign    : radio call sign
*/

const PROVIDERS = {
  noaa: {
    mmsi: "MMSI",
    vesselName: "VesselName",
    timestamp: "BaseDateTime",
    lat: "LAT",
    lng: "LON",
    speed: "SOG",
    heading: "Heading",
    course: "COG",
    vesselType: "VesselType",
    imo: "IMO",
    callSign: "CallSign",
  },

  marinetraffic: {
    mmsi: "MMSI",
    vesselName: "NAME",
    timestamp: "TIMESTAMP",
    lat: "LATITUDE",
    lng: "LONGITUDE",
    speed: "SPEED",
    heading: "HEADING",
    course: "COURSE",
    vesselType: "SHIPTYPE",
    imo: "IMO",
    callSign: "CALLSIGN",
  },

  spire: {
    mmsi: "mmsi",
    vesselName: "vessel_name",
    timestamp: "timestamp_utc",
    lat: "latitude",
    lng: "longitude",
    speed: "speed_knots",
    heading: "true_heading",
    course: "course_over_ground",
    vesselType: "vessel_class",
    imo: "imo",
    callSign: "call_sign",
  },
};

/*
  autoDetect — tries to figure out which provider the file came from
  by looking at the column headers.
  Returns the provider key (e.g. "noaa") or null if it can't tell.
*/
export function autoDetect(headers) {
  for (const [providerKey, mapping] of Object.entries(PROVIDERS)) {
    // Check if the timestamp column from this provider exists in the headers
    if (headers.includes(mapping.timestamp)) {
      return providerKey;
    }
  }
  return null;
}

/*
  getMapping — returns the column mapping for a given provider.
  Falls back to NOAA if the provider isn't recognized.
*/
export function getMapping(providerKey) {
  return PROVIDERS[providerKey] || PROVIDERS.noaa;
}

/*
  normalizeRow — takes a raw CSV row and a mapping, returns a clean standard object.
  Any field that doesn't exist in the raw row comes back as an empty string.
*/
export function normalizeRow(rawRow, mapping) {
  return {
    mmsi:       rawRow[mapping.mmsi]       || "",
    vesselName: rawRow[mapping.vesselName] || "",
    timestamp:  rawRow[mapping.timestamp]  || "",
    lat:        parseFloat(rawRow[mapping.lat]),
    lng:        parseFloat(rawRow[mapping.lng]),
    speed:      parseFloat(rawRow[mapping.speed]) || 0,
    heading:    parseFloat(rawRow[mapping.heading]) || 0,
    course:     parseFloat(rawRow[mapping.course])  || 0,
    vesselType: rawRow[mapping.vesselType] || "",
    imo:        rawRow[mapping.imo]        || "",
    callSign:   rawRow[mapping.callSign]   || "",
  };
}
